// ───────────────────────────────────────────────────────────────────────────
// 🔑 M1 — THE MLO LICENCES PAGE (Jesse + Hannah, 2026-11-01; backend routes/mloOfRecord, utils/mloOfRecord).
// /admin/settings/mlo-licences. Who can be the MLO of record: an active staff member with the Loan Officer role whose
// licence in the property state is active and unexpired. Each current licence says whether it is eligible, and if not,
// why (ended, expired, not linked to a staff login, no Loan Officer role).
// 🚨 INSERT-ONLY. Adding, ending and linking are each a NEW row on the server; nothing here edits or deletes a row, and
// the history below shows every row. Allow-listed (Hannah, Jesse): the controls show only to them, and the server
// refuses everyone else.
// ───────────────────────────────────────────────────────────────────────────
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import AdminLayout from '../components/AdminLayout';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const fmtDate = (d) => (d ? new Date(`${String(d).slice(0, 10)}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '');
const INPUT = 'border border-doorly-neutral-light rounded-lg px-2 py-1 text-sm';
const BTN = 'px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-doorly-blue disabled:bg-doorly-neutral-light';
const BTN_LINE = 'px-2 py-0.5 rounded border border-doorly-neutral-light text-xs text-doorly-blue';
const EMPTY = { staff_user_id: '', state: 'TX', nmls_id: '', first_name: '', last_name: '', status: 'active', expires_on: '', note: '' };

export default function AdminMloLicences() {
  const token = typeof window !== 'undefined' ? localStorage.getItem('doorly_admin_token') : null;
  const [info, setInfo] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [act, setAct] = useState(null);   // { kind: 'end'|'link', lic, reason, end_date, staff_user_id }
  const headers = { Authorization: `Bearer ${token}` };
  const load = useCallback(() => axios.get(`${API}/api/admin/mlo-licences`, { headers: { Authorization: `Bearer ${token}` } })
    .then((r) => setInfo(r.data)).catch(() => setInfo({ available: false })), [token]);
  useEffect(() => { if (token) load(); }, [token, load]);

  const post = async (path, body, after) => {
    setBusy(true); setMsg(null);
    try {
      await axios.post(`${API}/api/admin/${path}`, body, { headers });
      after(); load();
    } catch (e) {
      setMsg(e?.response?.data?.message || e?.response?.data?.error || 'That did not go through.');
    } finally { setBusy(false); }
  };
  const pickStaff = (id) => {
    const s = (info.staff || []).find((x) => String(x.id) === String(id));
    const [first, ...rest] = String((s && s.name) || '').split(' ');
    setForm((f) => ({ ...f, staff_user_id: id, first_name: f.first_name || first || '', last_name: f.last_name || rest.join(' ') }));
  };

  if (!info) return <AdminLayout><div className="p-8 text-sm text-doorly-slate">Loading…</div></AdminLayout>;
  const canEdit = !!info.canEdit;

  return (
    <AdminLayout>
      <div className="max-w-6xl mx-auto px-8 py-6" data-testid="mlo-licences">
        <h1 className="text-2xl font-extrabold text-doorly-navy mb-1">MLO licences</h1>
        <p className="text-sm text-doorly-slate mb-4">
          An application&apos;s MLO of record is picked from the eligible licences in its property state (round robin when
          there are several). Eligible: an active staff member with the Loan Officer role, and an active licence that has
          not expired. Every change is a new row — the history below keeps them all.
        </p>
        {!info.available && <div className="text-sm text-doorly-coral font-semibold">Licences cannot be read yet.</div>}
        {msg && <div data-testid="mlo-licences-error" className="text-sm text-doorly-coral font-semibold mb-3">{msg}</div>}

        <div className="bg-white rounded-xl border border-doorly-neutral-light p-4 mb-4">
          <div className="text-sm font-semibold text-doorly-navy mb-2">Current licences</div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-doorly-slate">
                <th className="py-1">MLO</th><th>NMLS</th><th>State</th><th>Status</th><th>Expires</th><th>Staff login</th><th>Eligible</th><th />
              </tr>
            </thead>
            <tbody>
              {(info.current || []).map((l) => (
                <tr key={l.id} data-testid={`mlo-licence-${l.nmlsId}-${l.state}`} className="border-t border-doorly-neutral-light align-top">
                  <td className="py-1 font-semibold text-doorly-navy">{l.firstName} {l.lastName}</td>
                  <td>{l.nmlsId}</td>
                  <td>{l.state}</td>
                  <td>{l.status === 'active' ? 'Active' : 'Ended'}</td>
                  <td>{l.expiresOn ? fmtDate(l.expiresOn) : 'No expiry on file'}</td>
                  <td>{l.staffName || <span className="text-doorly-orange font-semibold">Not linked</span>}</td>
                  <td data-testid="mlo-licence-eligible">
                    {l.eligible ? <span className="text-doorly-navy font-semibold">Yes</span>
                      : <span className="text-doorly-orange font-semibold">No — {l.notEligibleBecause}</span>}
                  </td>
                  <td className="text-right whitespace-nowrap">
                    {canEdit && l.status === 'active' && (
                      <button type="button" data-testid="mlo-licence-end" className={BTN_LINE}
                        onClick={() => setAct({ kind: 'end', lic: l, reason: '', end_date: info.today })}>End</button>
                    )}
                    {canEdit && (
                      <button type="button" data-testid="mlo-licence-link" className={`${BTN_LINE} ml-1`}
                        onClick={() => setAct({ kind: 'link', lic: l, reason: '', staff_user_id: l.staffUserId || '' })}>{l.staffUserId ? 'Relink' : 'Link'}</button>
                    )}
                  </td>
                </tr>
              ))}
              {!(info.current || []).length && <tr><td colSpan={8} className="py-2 text-doorly-slate">No licences on file.</td></tr>}
            </tbody>
          </table>

          {act && (
            <div className="flex flex-wrap items-center gap-2 mt-3" data-testid="mlo-licence-action">
              <span className="text-sm font-semibold text-doorly-navy">
                {act.kind === 'end' ? 'End' : 'Link'} {act.lic.firstName} {act.lic.lastName} · {act.lic.state} · NMLS {act.lic.nmlsId}
              </span>
              {act.kind === 'end' && (
                <input type="date" data-testid="mlo-licence-end-date" value={act.end_date} max={info.today}
                  onChange={(e) => setAct({ ...act, end_date: e.target.value })} className={INPUT} />
              )}
              {act.kind === 'link' && (
                <select data-testid="mlo-licence-link-staff" value={act.staff_user_id} onChange={(e) => setAct({ ...act, staff_user_id: e.target.value })} className={INPUT}>
                  <option value="">Pick a staff member…</option>
                  {(info.staff || []).map((s) => <option key={s.id} value={s.id}>{s.name}{s.isLoanOfficer ? '' : ' (no Loan Officer role)'}</option>)}
                </select>
              )}
              <input data-testid="mlo-licence-reason" value={act.reason} onChange={(e) => setAct({ ...act, reason: e.target.value })}
                placeholder="Why (recorded with your name)" className={INPUT} />
              <button type="button" data-testid="mlo-licence-confirm" className={BTN}
                disabled={busy || act.reason.trim().length < 5 || (act.kind === 'link' && !act.staff_user_id)}
                onClick={() => post(act.kind === 'end' ? 'mlo-licences/end' : 'mlo-licences/link',
                  act.kind === 'end' ? { nmls_id: act.lic.nmlsId, state: act.lic.state, end_date: act.end_date, reason: act.reason }
                    : { nmls_id: act.lic.nmlsId, state: act.lic.state, staff_user_id: act.staff_user_id, reason: act.reason },
                  () => setAct(null))}>{act.kind === 'end' ? 'End licence' : 'Link'}</button>
              <button type="button" className={BTN_LINE} onClick={() => setAct(null)}>Cancel</button>
            </div>
          )}
        </div>

        {canEdit && (
          <div className="bg-white rounded-xl border border-doorly-neutral-light p-4 mb-4" data-testid="mlo-licence-add">
            <div className="text-sm font-semibold text-doorly-navy mb-2">Add a licence</div>
            <div className="text-xs text-doorly-slate mb-2">
              The name as on the NMLS registry. Adding a licence for an NMLS ID and state already on file records a new current row (a renewal).
            </div>
            <div className="flex flex-wrap gap-2 items-center">
              <select data-testid="mlo-licence-staff" value={form.staff_user_id} onChange={(e) => pickStaff(e.target.value)} className={INPUT}>
                <option value="">Staff member…</option>
                {(info.staff || []).map((s) => <option key={s.id} value={s.id}>{s.name}{s.isLoanOfficer ? '' : ' (no Loan Officer role)'}</option>)}
              </select>
              <input data-testid="mlo-licence-first" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} placeholder="First name" className={INPUT} />
              <input data-testid="mlo-licence-last" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} placeholder="Last name" className={INPUT} />
              <input data-testid="mlo-licence-state" value={form.state} maxLength={2} onChange={(e) => setForm({ ...form, state: e.target.value.toUpperCase() })} placeholder="State" className={`${INPUT} w-16`} />
              <input data-testid="mlo-licence-nmls" value={form.nmls_id} onChange={(e) => setForm({ ...form, nmls_id: e.target.value.trim() })} placeholder="NMLS ID" className={`${INPUT} w-28`} />
              <select data-testid="mlo-licence-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={INPUT}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              <label className="text-xs text-doorly-slate flex items-center gap-1">Expires
                <input type="date" data-testid="mlo-licence-expires" value={form.expires_on} onChange={(e) => setForm({ ...form, expires_on: e.target.value })} className={INPUT} />
              </label>
              <input data-testid="mlo-licence-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Note" className={INPUT} />
              <button type="button" data-testid="mlo-licence-save" className={BTN}
                disabled={busy || !form.staff_user_id || !form.nmls_id || !form.state || !form.first_name || !form.last_name}
                onClick={() => post('mlo-licences', form, () => setForm(EMPTY))}>Add licence</button>
            </div>
          </div>
        )}
        {!canEdit && <div className="text-xs text-doorly-slate mb-4">Only Hannah and Jesse can add, end or link a licence.</div>}

        <div className="bg-white rounded-xl border border-doorly-neutral-light p-4">
          <div className="text-sm font-semibold text-doorly-navy mb-2">History (every row, newest first per licence)</div>
          <table className="w-full text-xs" data-testid="mlo-licences-history">
            <thead>
              <tr className="text-left text-doorly-slate"><th className="py-1">Recorded</th><th>MLO</th><th>NMLS</th><th>State</th><th>Status</th><th>Expires / ended</th><th>Staff login</th><th>Note</th></tr>
            </thead>
            <tbody>
              {(info.history || []).map((h) => (
                <tr key={h.id} className="border-t border-doorly-neutral-light align-top">
                  <td className="py-1">{fmtDate(h.createdAt)}</td>
                  <td>{h.firstName} {h.lastName}</td>
                  <td>{h.nmlsId}</td>
                  <td>{h.state}</td>
                  <td>{h.status === 'active' ? 'Active' : 'Ended'}</td>
                  <td>{h.expiresOn ? fmtDate(h.expiresOn) : ''}</td>
                  <td>{h.staffUserId ? `#${h.staffUserId}` : ''}</td>
                  <td>{h.note || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
}
